import { Tabs } from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"

import { PageShell, PageSkeleton } from "@/components/Layout"
import Appearance from "@/components/UserSettings/Appearance"
import ChangePassword from "@/components/UserSettings/ChangePassword"
import UserInformation from "@/components/UserSettings/UserInformation"
import useAuth from "@/hooks/useAuth"
import { useStudentLabels } from "@/hooks/useStudentLabels"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { isEmbeddedSession } from "@/utils"

const tabsConfig = [
  { value: "my-profile", title: "My profile", component: UserInformation },
  { value: "password", title: "Password", component: ChangePassword },
  { value: "appearance", title: "Appearance", component: Appearance },
  // { value: "danger-zone", title: "Danger zone", component: DeleteAccount },
]

export const Route = createFileRoute("/_layout/settings")({
  component: UserSettings,
})

function UserSettings() {
  const { settingsLabel } = useStudentLabels()
  const { user: currentUser } = useAuth()
  const isEmbed = isEmbeddedSession()
  const breadcrumbs = useRouteBreadcrumbs("settings.index")

  // Filter tabs based on user type:
  // - Superusers: hide "Danger zone" tab
  // - Embedded users: hide "Password" and "Danger zone" tabs
  const finalTabs = (() => {
    if (isEmbed) {
      // Embedded users: show only "My profile" and "Appearance"
      return tabsConfig.filter(
        (tab) => tab.value !== "password" && tab.value !== "danger-zone",
      )
    }
    if (currentUser?.is_superuser) {
      // Superusers: hide only "Danger zone"
      return tabsConfig.slice(0, 3)
    }
    // Regular users: show all tabs
    return tabsConfig
  })()

  if (!currentUser) {
    return (
      <PageShell
        title={settingsLabel}
        description="Manage your profile preferences"
        breadcrumbs={breadcrumbs}
      >
        <PageSkeleton variant="detail" rows={6} />
      </PageShell>
    )
  }

  return (
    <PageShell
      title={settingsLabel}
      description="Manage your profile preferences"
      breadcrumbs={breadcrumbs}
    >
      <Tabs.Root defaultValue="my-profile" variant="subtle">
        <Tabs.List>
          {finalTabs.map((tab) => (
            <Tabs.Trigger key={tab.value} value={tab.value}>
              {tab.title}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {finalTabs.map((tab) => (
          <Tabs.Content key={tab.value} value={tab.value}>
            <tab.component />
          </Tabs.Content>
        ))}
      </Tabs.Root>
    </PageShell>
  )
}
